import ApiCollection      from '@widesk-core/models/ApiCollection';
import MapModel           from '@models/map/MapModel';
import ProjectModel       from '@models/map/ProjectModel';
import { action }         from 'mobx';
import { makeObservable } from 'mobx';
import { observable }     from 'mobx';
import { runInAction }    from 'mobx';
import { computed }       from 'mobx';
import { FormModal }      from '@widesk-ui/components/modal/FormModal';
import MapForm            from '@forms/map/MapForm';
import ModalTitle        from '@/components/ModalTitle';
import { EnvironmentOutlined } from '@ant-design/icons';
import { navigate }       from '@widesk-ui/hooks/useNavigate';
import FeatureModel        from '@/models/services/map/FeatureModel';
import appStore            from '@/stores/appStore';
import ProjectMercureListener from '@/TokamapEditor/stores/ProjectMercureListener';
import projectPublicationStore from '@/TokamapEditor/stores/projectPublicationStore';
import { getIdFromUrn }     from '@widesk-core/tools/identifier';
import { isUrn }           from '@widesk-core/tools/identifier';
import { refreshMapUsages } from '@/components/Functionality/refreshMapUsages';

// Le menu des cartes n'a plus qu'un point d'ouverture (le bouton flottant sur le plan) ; le
// déclencheur reste tracé pour piloter l'état ouvert/fermé du popover.
export type MapMenuTrigger = 'button';

/** `byUserUrn` reste vide quand la modification est la nôtre : on n'écrit pas "par soi-même". */
export type LastModification = { at: Date; byUserUrn?: Urn; mapUrn: Urn };

/**
 * L'auteur d'une modification tel qu'il sert au message : l'URN brute de l'attribut, et `undefined`
 * quand c'est nous — on n'écrit pas "par soi-même". L'attribut brut plutôt que le resolvable
 * `Blamable` du modèle : on veut l'URN, pas un modèle en cours de résolution, et c'est
 * `authorNameStore` qui se charge d'en tirer un nom au moment de l'affichage.
 */
function readAuthorUrn(updatedBy: unknown): Urn | undefined {
	if (typeof updatedBy !== 'string' || !isUrn(updatedBy)) return undefined;
	if (String(getIdFromUrn(updatedBy as Urn)) === String(appStore.user.id)) return undefined;

	return updatedBy as Urn;
}

export default class ProjectEditorStore {
	currentMap = new MapModel();
	mapCollection = new ApiCollection(MapModel);
	project = new ProjectModel();
	mapMenuOpenedFrom: MapMenuTrigger | null = null;

	// Dernière modification connue de CHAQUE carte du projet. Tenue par carte et non en une seule
	// valeur globale parce que les deux affichages en ont besoin séparément : la barre du haut veut le
	// maximum (voir `lastModification`), le menu des cartes veut la date de chaque ligne. Alimentée par
	// nos propres enregistrements (`SaveStatusBanner`), par les évènements des autres utilisateurs
	// (`ProjectMercureListener`) et, au chargement, par ce que l'API sait déjà (`_loadLastModification`).
	lastModificationByMapUrn = observable.map<Urn, LastModification>({}, { deep: false });

	// Suivi en direct des modifications de TOUTES les cartes du projet — `EditorMercureListener`, lui,
	// n'écoute que la carte ouverte, puisque c'est la seule qu'il ait à redessiner.
	private readonly _mercure = new ProjectMercureListener(modification => this.setLastModification(modification));

	private readonly _projectId: id;

	constructor(projectId: id) {
		this._projectId = projectId;
		this.project.setId(projectId);
		this.mapCollection.setRequiredFilter('project', projectId).setSort('position', 'asc');

		makeObservable(this, {
			currentMap: observable,
			setCurrentMap: action,

			mapMenuOpenedFrom: observable,
			setMapMenuOpenedFrom: action,

			lastModificationByMapUrn: observable,
			setLastModification: action,
			mergeLastModification: action,
			lastModification: computed,
		});
	}

	async fetch() {
		await Promise.all([
			this.project.fetch(),
			this.mapCollection.list(),
		]);

		if (!this.mapCollection.length) {
			const project = new ProjectModel({ id: this._projectId });
			await project.fetch();

			const newMap = new MapModel();

			await FormModal.openAsync({
				children: (
					<MapForm
						model={newMap}
						projectUrn={project.urn}
						onSuccess={() => {
							this.mapCollection.add(newMap);
							void refreshMapUsages();
						}}
					/>
				),
				title: <ModalTitle icon={<EnvironmentOutlined />}>Ajouter une première {MapModel.localize('a')}</ModalTitle>,
				width: 760,
			});

			if (!newMap.id) {
				navigate('/');
				return;
			}
		}

		const map = this.mapCollection.find(m => `#${m.id}` === location.hash) || this.mapCollection.first()!;

		// Remplacement et non empilement pour CETTE sélection-là : elle est automatique, elle ne
		// correspond à aucune action de l'utilisateur, et l'empiler créait une deuxième entrée pour la
		// simple ouverture du projet — le premier "précédent" ne faisait alors que retirer le hash.
		this.setCurrentMap(map, true);

		window.addEventListener('hashchange', this._onHashChange);

		// Volontairement pas attendu : l'éditeur n'a pas à retarder son ouverture pour un message.
		void this._loadLastModification();

		// Une publication lancée juste avant un rechargement, ou depuis un autre onglet, peut être
		// encore en cours : le bouton et l'historique doivent l'annoncer sans attendre qu'on republie.
		void projectPublicationStore.load(this._projectId)
			.catch(err => console.error('Dernière publication du projet', err));
	}

	/**
	 * Abonne le suivi en direct aux cartes actuellement connues du projet — idempotent.
	 *
	 * Les cartes sans identifiant sont écartées : la collection peut contenir un modèle pas encore
	 * enregistré (ajout en cours), dont l'URN serait tronquée — la demande de jeton partirait alors
	 * sur `/maps//mercure`, que l'API rejette.
	 */
	syncMercure() {
		this._mercure.sync(this.mapCollection.models.filter(map => !!map.id).map(map => map.urn));
	}

	/** Une modification qui vient d'arriver : elle est par définition la plus récente de sa carte. */
	setLastModification(modification: LastModification) {
		this.lastModificationByMapUrn.set(modification.mapUrn, modification);
	}

	/**
	 * Une modification apprise après coup (chargement), qui ne doit écraser ce qui est affiché que si
	 * elle est réellement plus récente : les sources du chargement arrivent dans le désordre, et une
	 * modification faite entre-temps dans la session doit primer.
	 */
	mergeLastModification(modification: LastModification) {
		const known = this.lastModificationByMapUrn.get(modification.mapUrn);

		if (known && known.at >= modification.at) return;

		this.lastModificationByMapUrn.set(modification.mapUrn, modification);
	}

	/** Celle d'une carte précise — pour sa ligne dans le menu des cartes. */
	getLastModification(mapUrn: Urn): LastModification | null {
		return this.lastModificationByMapUrn.get(mapUrn) || null;
	}

	/** La plus récente du projet, toutes cartes confondues — pour la barre du haut. */
	get lastModification(): LastModification | null {
		let latest: LastModification | null = null;

		this.lastModificationByMapUrn.forEach(modification => {
			if (!latest || modification.at > latest.at) latest = modification;
		});

		return latest;
	}

	/**
	 * Reconstitue la dernière modification de chaque carte à l'ouverture, pour qu'un simple F5 les
	 * affiche au lieu d'attendre les premiers enregistrements de la session. Deux sources, car l'API
	 * les sépare :
	 *
	 * - les ressources `Map` elles-mêmes (titre, vue par défaut, fond) — déjà chargées par `fetch()`,
	 *   donc gratuites, et les seules à porter un auteur (`Blamable`) ;
	 * - la `Feature` la plus récemment modifiée de chaque carte, car dessiner, déplacer ou supprimer
	 *   une forme n'écrit PAS la carte : sans elle, une carte dessinée toute la journée affichait la
	 *   date à laquelle on l'avait renommée des mois plus tôt.
	 *
	 * Une requête par carte, d'UNE ligne (`itemsPerPage: 1`, trié par date décroissante) : c'est un
	 * maximum, pas une liste. Elles partent en parallèle et ne bloquent pas l'ouverture de l'éditeur.
	 * Un seul appel couvrant tout le projet donnerait bien le maximum global, mais pas la date de
	 * chaque ligne du menu des cartes, qui est justement ce qu'on cherche ici.
	 *
	 * Par `params` et non `setFilter`/`setSort` : `FeatureModel` ne déclare ni filtre ni tri, alors que
	 * l'endpoint accepte bien un `map` — c'est déjà comme ça que `TokamapEditor/index.tsx` enregistre
	 * les formes en lot.
	 */
	private async _loadLastModification() {
		this.mapCollection.models.forEach(map => {
			// `map.attributes.updatedAt` et non `map.updatedAt` : le getter de `TimestampAble` retombe
			// sur `dayjs()` quand l'attribut est absent, ce qui afficherait "à l'instant" pour une carte
			// jamais modifiée.
			if (!map.attributes.updatedAt) return;

			this.mergeLastModification({
				at: map.updatedAt.toDate(),
				byUserUrn: readAuthorUrn(map.attributes.updatedBy),
				mapUrn: map.urn,
			});
		});

		await Promise.all(
			this.mapCollection.models
				.filter(map => !!map.id)
				.map(map => this._loadLastFeatureModification(map)),
		);
	}

	private async _loadLastFeatureModification(map: MapModel) {
		try {
			const collection = new ApiCollection(FeatureModel);

			await collection.list({
				params: { 'map': map.id, 'itemsPerPage': 1, 'order[updatedAt]': 'desc' },
			});

			const feature = collection.models.at(0);

			if (!feature?.attributes.updatedAt) return;

			this.mergeLastModification({
				at: feature.updatedAt.toDate(),
				byUserUrn: readAuthorUrn(feature.attributes.updatedBy),
				mapUrn: map.urn,
			});
		} catch (err) {
			// Sans conséquence : on garde la date de la carte, au pire trop ancienne.
			console.error(`Dernière modification de la carte "${map.label}"`, err);
		}
	}

	/**
	 * `replaceHistory` pour une sélection que l'utilisateur n'a pas demandée (l'ouverture du projet) :
	 * elle ne mérite pas sa propre entrée d'historique. Un changement de carte volontaire, lui, en
	 * empile bien une, pour que "précédent" ramène à la carte précédente avant de quitter le projet.
	 */
	setCurrentMap(map: MapModel, replaceHistory = false) {
		this._applyCurrentMap(map);

		if (!map.id) return;

		if (replaceHistory) window.history.replaceState(null, '', `#${map.id}`);
		else window.location.hash = map.id;
	}
	/**
	 * Ouvre une carte sans toucher à l'historique — c'est la moitié commune à une sélection demandée
	 * (`setCurrentMap`) et à un retour en arrière du navigateur (`_onHashChange`), qui ne doit surtout
	 * pas réécrire l'entrée vers laquelle il vient de revenir.
	 */
	private _applyCurrentMap(map: MapModel) {
		// Seul point traversé aussi bien à l'ouverture du projet qu'après l'ajout d'une carte (voir
		// `MapMenu`), donc le bon endroit pour tenir les abonnements à jour sans que les appelants
		// aient à y penser.
		this.syncMercure();

		// `runInAction` plutôt qu'une annotation dans `makeObservable` : celui-ci n'accepte pas de
		// membre privé dans sa table d'annotations.
		runInAction(() => {
			this.currentMap = map;
			this.setMapMenuOpenedFrom(null);
		});
	}

	/**
	 * "Précédent"/"Suivant" du navigateur entre deux cartes. Sans cet écouteur, empiler une entrée par
	 * changement de carte ne servirait à rien : l'URL reviendrait en arrière, mais l'éditeur
	 * continuerait d'afficher la carte courante.
	 */
	private _onHashChange = () => {
		const map = this.mapCollection.find(m => `#${m.id}` === location.hash);

		// Le hash que nous venons d'écrire nous-mêmes déclenche aussi cet évènement : sans cette
		// comparaison, on rouvrirait la carte déjà ouverte à chaque changement.
		if (!map || String(map.id) === String(this.currentMap.id)) return;

		this._applyCurrentMap(map);
	};

	async reorderMaps(ordered: MapModel[]) {
		const previous = ordered.map(map => map.position);
		const moved = ordered.filter((map, index) => map.position !== index);

		runInAction(() => ordered.forEach((map, index) => map.set('position', index)));

		try {
			for (const map of moved) {
				await map.patch({ position: ordered.indexOf(map) });
			}
		} catch (err) {
			runInAction(() => ordered.forEach((map, index) => map.set('position', previous[index])));

			throw err;
		}
	}

	setMapMenuOpenedFrom(trigger: MapMenuTrigger | null) {
		this.mapMenuOpenedFrom = trigger;
	}

	clear() {
		window.removeEventListener('hashchange', this._onHashChange);
		projectPublicationStore.clear();
		this._mercure.stop();
		this.mapCollection.clear();
		this.currentMap.clear();
		this.project.clear();
	}
}